// Touch numeric PIN pad. Renders 4 masked dots + a 0-9 keypad with
// backspace and OK. Auto-submits once the 4th digit is entered.

import { useCallback, useEffect, useState } from 'react';
import styles from './kiosk.module.css';

export interface KioskPinPadProps {
  /** Heading shown above the dots (e.g. the staff member's name). */
  readonly title: string;
  /** Called with the 4-digit PIN once entry completes. */
  readonly onSubmit: (pin: string) => void;
  /** Back to the staff picker. */
  readonly onBack: () => void;
  /** Error message to display; a non-empty value triggers the shake + clear. */
  readonly error?: string | null;
  /** Disables input (e.g. while a request is in flight or during lockout). */
  readonly disabled?: boolean;
}

const KEYS: ReadonlyArray<string> = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function KioskPinPad({ title, onSubmit, onBack, error, disabled }: KioskPinPadProps) {
  const [digits, setDigits] = useState('');
  const [shake, setShake] = useState(false);

  // When the parent reports an error, shake and clear the entry.
  useEffect(() => {
    if (error) {
      setShake(true);
      setDigits('');
      const t = setTimeout(() => setShake(false), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [error]);

  const append = useCallback(
    (d: string) => {
      if (disabled) return;
      setDigits((prev) => {
        if (prev.length >= 4) return prev;
        const next = prev + d;
        if (next.length === 4) {
          // Defer so the 4th dot paints before the submit handler runs.
          setTimeout(() => onSubmit(next), 0);
        }
        return next;
      });
    },
    [disabled, onSubmit],
  );

  const backspace = useCallback(() => {
    if (disabled) return;
    setDigits((prev) => prev.slice(0, -1));
  }, [disabled]);

  return (
    <div className={styles.pinWrap}>
      <h2 className={styles.screenTitle}>{title}</h2>
      <div
        className={`${styles.pinDots} ${shake ? styles.shake : ''}`}
        aria-label="PIN entry"
        data-testid="kiosk-pin-dots"
      >
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`${styles.pinDot} ${i < digits.length ? styles.pinDotFilled : ''}`}
          />
        ))}
      </div>
      <div className={styles.error} role="alert">
        {error ?? ''}
      </div>
      <div className={styles.pinPad}>
        {KEYS.map((k) => (
          <button
            key={k}
            type="button"
            className={styles.pinKey}
            onClick={() => append(k)}
            disabled={disabled}
            aria-label={`Digit ${k}`}
          >
            {k}
          </button>
        ))}
        <button type="button" className={styles.pinKey} onClick={onBack} aria-label="Back">
          ←
        </button>
        <button
          type="button"
          className={styles.pinKey}
          onClick={() => append('0')}
          disabled={disabled}
          aria-label="Digit 0"
        >
          0
        </button>
        <button
          type="button"
          className={styles.pinKey}
          onClick={backspace}
          disabled={disabled}
          aria-label="Delete"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}
